// Helper chung cho 3 panel checkout POS (quick / delivery / modal): tính giá vật
// tư HIỆU LỰC theo trạng thái tách/gộp của dòng SP (line_key = product_id vì POS
// gộp trùng sản phẩm về 1 dòng). Dòng GỘP → giá vật tư = 0 (nằm trong giá SP).
import type { PosOrderItem } from './pos-types'
import type { PackagingDraftItem } from '@/lib/packaging-draft'
import type { LineSplitState } from '@/lib/material-split'
import type { MoneyLine } from '@/lib/order-money'
import { effectiveMaterialUnitPrice } from '@/lib/material-split'
import { vatFromGross } from '@/lib/order-money'

const DEFAULT_STATE: LineSplitState = {
    priceIncludesMaterial: false,
    materialPriceSplit: false,
}

function lineSplitLookup(items: PosOrderItem[]): Map<string, LineSplitState> {
    const map = new Map<string, LineSplitState>()
    for (const it of items) {
        map.set(it.product_id, {
            priceIncludesMaterial: it.price_includes_material ?? false,
            materialPriceSplit: it.material_price_split ?? false,
        })
    }
    return map
}

/**
 * Trả `priceOf(row)` (giá vật tư hiệu lực của 1 dòng vật tư) + `total` (tổng tiền
 * vật tư đã áp trạng thái tách/gộp + CK dòng). Build lookup 1 lần cho hiệu năng.
 */
/**
 * Index dòng đơn (theo mảng items gửi lên) mà 1 dòng vật tư thuộc về. POS
 * gộp trùng SP về 1 dòng nên line_key = product_id. Rỗng / không tìm thấy
 * (vật tư thêm tay) → undefined = vật tư mức đơn.
 */
export function posOrderItemIndex(
    items: PosOrderItem[],
    lineKey: string,
): number | undefined {
    if (!lineKey) return undefined
    const idx = items.findIndex((i) => i.product_id === lineKey)
    return idx >= 0 ? idx : undefined
}

export function posMaterialEffective(
    items: PosOrderItem[],
    packagingItems: PackagingDraftItem[] | undefined,
): { priceOf: (row: PackagingDraftItem) => number; total: number } {
    const lookup = lineSplitLookup(items)
    const priceOf = (row: PackagingDraftItem) =>
        effectiveMaterialUnitPrice(
            row.unit_price ?? 0,
            lookup.get(row.line_key) ?? DEFAULT_STATE,
        )
    const total = (packagingItems ?? []).reduce(
        (s, p) =>
            s +
            priceOf(p) *
                (p.quantity ?? 0) *
                (1 - (p.discount_percent ?? 0) / 100),
        0,
    )
    return { priceOf, total }
}

export interface PosOrderTotals {
    /** Tiền hàng sau CK dòng (chưa gồm vật tư). */
    itemSubtotal: number
    /** Tiền vật tư hiệu lực sau CK dòng. */
    materialTotal: number
    /** Hàng + vật tư, trước chiết khấu đơn. */
    baseAmount: number
    /** Sau chiết khấu đơn — base tính VAT. */
    subtotal: number
    vatAmount: number
    totalAmount: number
}

/**
 * Tổng tiền đơn POS — nguồn DUY NHẤT cho panel thanh toán lẫn dòng "Tổng tiền
 * hàng" bên trái, để hai nơi luôn cùng một số VAT. Vật tư gộp vào base cùng SP
 * TRƯỚC chiết khấu đơn + VAT; VAT suy từ giá gồm VAT (M-78) như snapshot gửi BE.
 */
export function posOrderTotals(
    items: PosOrderItem[],
    packagingItems: PackagingDraftItem[] | undefined,
    orderDiscountPercent: number,
    vatPercent: number,
): PosOrderTotals {
    const itemSubtotal = items.reduce(
        (s, i) =>
            s + i.quantity * i.unit_price * (1 - i.discount_percent / 100),
        0,
    )
    const { priceOf, total: materialTotal } = posMaterialEffective(
        items,
        packagingItems,
    )
    const baseAmount = itemSubtotal + materialTotal
    const subtotal =
        orderDiscountPercent > 0
            ? baseAmount * (1 - orderDiscountPercent / 100)
            : baseAmount
    // M-78: VAT = tổng theo giá gồm VAT từng đơn vị − tiền hàng làm tròn, để
    // tổng tròn theo giá niêm yết và khớp đúng tổng server tính.
    const lines: MoneyLine[] = [
        ...items.map((i) => ({
            quantity: i.quantity,
            unitPrice: i.unit_price,
            discountPercent: i.discount_percent,
        })),
        ...(packagingItems ?? []).map((p) => ({
            quantity: p.quantity ?? 0,
            unitPrice: priceOf(p),
            discountPercent: p.discount_percent ?? 0,
        })),
    ]
    const base = Math.round(subtotal)
    const vatAmount = vatFromGross(
        lines,
        orderDiscountPercent,
        vatPercent,
        base,
    )
    const totalAmount = base + vatAmount
    return {
        itemSubtotal,
        materialTotal,
        baseAmount,
        subtotal,
        vatAmount,
        totalAmount,
    }
}
